import { onKeyDown } from '@prezly/slate-lists';
import classnames from 'classnames';
import isHotkey, { isKeyHotkey } from 'is-hotkey';
import React, { useEffect, useMemo, useState } from 'react';
import { BaseSelection, Descendant, Editor, Range, Transforms } from 'slate';
import { Editable, ReactEditor, Slate } from 'slate-react';
import withGenerateClassName from '../../../themes/withGenerateClassName';
import CodeToText from './Elements/CodeToText/CodeToText';
import { IVariableChip } from './modules/RTEVariablesModal/modules/VariableChip/VariableChip.interfaces';
import RTEVariablesModal from './modules/RTEVariablesModal/RTEVariablesModal';
import { IRTEVariablesModal } from './modules/RTEVariablesModal/RTEVariablesModal.interfaces';
import { createSlateEditor } from './plugins';
import { useStyles } from './RichTextEditorV2.styles';
import { allowedStylesOnPaste } from './RichTextEditorV2.utils';
import Toolbar from './Toolbar/Toolbar';
import {
  convertStringToCSSObject,
  flattenHtmlWithStyles,
} from './utils/FilterPasteElements/filterPasteElements';
import {
  HOTKEYS,
  renderElement,
  renderLeaf,
  toggleMark,
  TYPES,
} from './utils/SlateUtilityFunctions.js';

export interface RTEProps {
  editorState: any;
  onEditorStateChange: (value: Descendant[]) => void;
  toolbarPosition: 'top' | 'bottom';
  showBorder: boolean;
  isSmallToolbar: boolean;
  minHeight?: string;
  maxHeight?: string;
  padding?: string;
  slateProps?: any;
  editableProps?: any;
  editableWrapperProps?: any;
  rootWrapperProps?: any;
  rootWrapperClassName?: string;
  editableWrapperClassName?: string;
  additionalToolbarActions?: any[];
  customToolbarClassName?: string;
  onFocus?: Function;
  onBlur?: Function;
  codeToTextTextareaProps?: any;
  imageUploadCallback?: (val: React.SyntheticEvent | string) => null;
  cursorPlacementTimeout?: number;
  autoFocus?: boolean;
  variablesProps?: Partial<IRTEVariablesModal>;
  readOnly?: boolean;
  showToolbar: boolean;
  isBoxDisabled: boolean;
  removeStylesOnPaste: boolean;
  pasteOnlyText?: boolean;
  filterToolbarGroups?: string[];
}

export interface HtmlActionProps {
  showInput?: boolean;
  html?: string;
  action?: string;
  location?: string;
}

const RichTextEditorV2: React.FC<RTEProps> = ({
  editorState: value,
  onEditorStateChange: onChange,
  showBorder = true,
  minHeight,
  maxHeight,
  padding,
  isSmallToolbar,
  toolbarPosition = 'top',
  slateProps,
  editableProps,
  editableWrapperProps,
  rootWrapperProps,
  onFocus,
  onBlur,
  rootWrapperClassName,
  editableWrapperClassName,
  additionalToolbarActions,
  customToolbarClassName,
  codeToTextTextareaProps,
  imageUploadCallback,
  cursorPlacementTimeout = 300,
  autoFocus,
  showToolbar = true,
  isBoxDisabled = false, //this prop is used to check if its a normal rte or rte displaye for an rte modal
  removeStylesOnPaste = false,
  pasteOnlyText = true,
  filterToolbarGroups = [],
  variablesProps,
  ...restProps
}) => {
  const { readOnly } = restProps;
  const isReadOnly = readOnly || isBoxDisabled;

  const classes = useStyles({
    showBorder,
    toolbarPosition,
    minHeight,
    maxHeight,
    padding,
    isBoxDisabled,
  });

  const editor = useMemo(() => createSlateEditor(), []);

  const ua = navigator.userAgent.toLowerCase();
  const isAndroid = ua.indexOf('android') > -1; //&& ua.indexOf("mobile");

  setTimeout(() => {
    const editorEl = document.querySelector<HTMLDivElement>(
      '[data-slate-editor="true"]'
    );

    if (!editorEl) return;

    editorEl.style.minHeight = minHeight || `153px`;
    editorEl.style.maxHeight = maxHeight || `auto`;
  }, cursorPlacementTimeout);

  const EmptyState = [{ type: TYPES.PARAGRAPH, children: [{ text: '' }] }];
  editor.children = value || EmptyState;

  const [htmlAction, setHtmlAction] = useState<HtmlActionProps>({
    showInput: false,
    html: '',
    action: '',
    location: '',
  });

  const handleCodeToText = (partialState: HtmlActionProps) => {
    setHtmlAction((prev) => ({
      ...prev,
      ...partialState,
    }));
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    for (const hotkey in HOTKEYS) {
      if (isHotkey(hotkey, event as any)) {
        event.preventDefault();
        const mark = HOTKEYS[hotkey];
        toggleMark(editor, mark);
      }
    }
  };

  function handlePaste(event: React.ClipboardEvent<HTMLDivElement>) {
    /* 
      Remove Styles on paste removes all styles from the copied content
      if removeStylesOnPaste is false -> and pasteOnlyText is true -> then pasting behaviour will be filtering out images and keeping text with its styles.
      if both are false then the pasting behaviour is default.
    */
    if (removeStylesOnPaste) {
      handlePasteRemoveStyles(event);
    } else {
      if (pasteOnlyText) {
        handlePasteOnlyText(event);
      }
    }
  }

  //has to be inside slatejs as we are using Transforms
  function handlePasteRemoveStyles(
    event: React.ClipboardEvent<HTMLDivElement>
  ) {
    event.preventDefault();
    const formattedText = event.clipboardData.getData('text/plain');

    Transforms.insertText(editor, formattedText);
  }
  /**
   * Check if there is text alignment or anchor tags present in the given HTML data.
   * @param {string} htmlData - The HTML data to be checked.
   * @returns {boolean} True if text alignment is present and there are no anchor tags, otherwise false.
   */
  function isTextAlignmentOnPasting(htmlData: string) {
    // Check if the 'alignment' feature is enabled in the toolbar
    if (
      filterToolbarGroups.length &&
      filterToolbarGroups.includes('alignment')
    ) {
      // Create a temporary DOM element to work with the HTML data
      const tempElement = document.createElement('div');
      tempElement.innerHTML = htmlData;

      // Check if any elements have 'text-align' styles applied
      const elementsWithTextAlign = tempElement.querySelectorAll(
        '[style*="text-align"]'
      );

      // Check if the HTML content contains anchor tags
      const anchorTags = tempElement.querySelectorAll('a');

      /**
       *  If anchor tags are found, return false,
       *  because in handlePasteOnlyText, changes the anchor "<a>" to "<p>"
       *  and that will remmove the functionality of anchor tag and converted to paragraph
       */
      if (anchorTags.length > 0) {
        return false;
      }

      // If no anchor tags are found, check if 'text-align' styles are present
      return elementsWithTextAlign.length > 0;
    }

    // If the 'alignment' feature is not enabled, return false
    return false;
  }

  function handlePasteOnlyText(event: React.ClipboardEvent<HTMLDivElement>) {
    let htmlData = event.clipboardData.getData('text/html');

    const containsImages = htmlData && /<img[\s\S]*?>/i.test(htmlData);
    // checking for the alignment style in htmlString when we remove the Alignment from the RTE toolbar
    //then have to remove the text-align style from the html String
    if (
      pasteOnlyText &&
      (containsImages || isTextAlignmentOnPasting(htmlData))
    ) {
      event.preventDefault();
      let allowedStyles = [...allowedStylesOnPaste];
      if (
        filterToolbarGroups.length &&
        filterToolbarGroups.includes('alignment')
      ) {
        allowedStyles = allowedStylesOnPaste.filter(
          (style) => style !== 'text-align'
        );
      }
      const filteredData = flattenHtmlWithStyles(htmlData, allowedStyles);
      const div = document.createElement('div');
      div.innerHTML = filteredData;

      const children = Array.from(div.children);

      // TODO: check if this can be converted as RTE state, and inserted at once.
      for (const child of children) {
        if (child.textContent) {
          Transforms.insertNodes(
            editor,
            {
              type: TYPES.PARAGRAPH,
              children: [{ text: child.textContent || '' }],
              styles: convertStringToCSSObject(
                child.getAttribute('style') || ''
              ),
            },
            {
              select: true,
            }
          );
        }
      }
    }
  }

  const { anchorEl, onClickVariable, onClose } = variablesProps ?? {};

  const handleVariableChipClick = (chip: IVariableChip) => {
    const { value } = chip;
    Editor.insertText(editor, value);

    if (onClickVariable) onClickVariable(chip);
  };

  const handleCloseVariablesModal: IRTEVariablesModal['onClose'] = (
    ...args
  ) => {
    if (onClose) onClose(...args);

    // set focus to editor
    setTimeout(() => {
      if (lastSelection) Transforms.select(editor, lastSelection);
      ReactEditor.focus(editor as ReactEditor);
    }, 50);
  };

  const [lastSelection, setLastSelection] = useState<BaseSelection>();

  useEffect(() => {
    // Focus on the RTE editor after closing the comms variables picker
    const handleFocusOnVariablesModalClose = () => {
      if (anchorEl && editor) setLastSelection(editor.selection);
    };

    handleFocusOnVariablesModalClose();
  }, [anchorEl]);

  return (
    <div
      className={classnames(classes.root, rootWrapperClassName)}
      {...rootWrapperProps}
    >
      <Slate
        editor={editor}
        initialValue={value || EmptyState}
        onChange={onChange}
        {...slateProps}
        {...restProps}
      >
        {!isReadOnly && showToolbar && (
          <Toolbar
            handleCodeToText={handleCodeToText}
            toolbarPosition={toolbarPosition}
            isSmallToolbar={isSmallToolbar}
            additionalToolbarActions={additionalToolbarActions}
            customToolbarClassName={customToolbarClassName}
            imageUploadCallback={imageUploadCallback}
            filterToolbarGroups={filterToolbarGroups}
          />
        )}
        <div
          className={classnames(classes.editable, editableWrapperClassName)}
          {...editableWrapperProps}
        >
          <Editable
            placeholder="Write something"
            renderElement={(props) =>
              renderElement({
                ...props,
                readOnly: isReadOnly,
              })
            }
            autoFocus={autoFocus}
            renderLeaf={renderLeaf}
            onPaste={handlePaste}
            autoCorrect="false"
            spellCheck="false"
            autoComplete="false"
            autoCapitalize="none"
            keyboardType="visible-password"
            onKeyDown={(event) => {
              onKeyDown(editor, event);

              handleKeyDown(event);

              const { selection } = editor;

              if (selection && Range.isCollapsed(selection)) {
                const { nativeEvent } = event;
                if (isKeyHotkey('left', nativeEvent)) {
                  event.preventDefault();
                  Transforms.move(editor, { unit: 'offset', reverse: true });
                  return;
                }
                if (isKeyHotkey('right', nativeEvent)) {
                  event.preventDefault();
                  Transforms.move(editor, { unit: 'offset' });
                  return;
                }
              }
            }}
            onDOMBeforeInput={(event) => {
              if (isAndroid) {
                const {
                  // isComposing,
                  inputType,
                } = event;

                if (inputType === 'deleteContentBackward') {
                  event.preventDefault();
                  Transforms.delete(editor, {
                    unit: 'character',
                    distance: 1,
                    reverse: true,
                  });

                  return;
                }
              }
            }}
            {...editableProps}
            {...restProps}
            className={classnames(
              classes.editableRoot,
              editableProps?.className
            )}
            readOnly={isReadOnly}
          />
        </div>
        {htmlAction.showInput && (
          <CodeToText
            {...htmlAction}
            handleCodeToText={handleCodeToText}
            codeToTextTextareaProps={codeToTextTextareaProps}
          />
        )}
      </Slate>

      {variablesProps ? (
        <RTEVariablesModal
          {...variablesProps}
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClickVariable={handleVariableChipClick}
          onClose={handleCloseVariablesModal}
        />
      ) : null}
    </div>
  );
};

export default withGenerateClassName(RichTextEditorV2);
